import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { fetchSchema, fetchBeliefs } from "@/lib/api";
import { createPromotion, updatePromotion } from "@/lib/promotions";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

function ymOf(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function currentYM() {
  return ymOf(new Date());
}

export function PromotionEditor({ open, onClose, promotion, onSaved }) {
  const isEdit = !!promotion?.id;
  const [schema, setSchema] = useState(null);
  const [beliefs, setBeliefs] = useState([]);
  const [form, setForm] = useState({
    month: currentYM(),
    promotion_name: "",
    offer_tag: "",
    headline_belief_id: "",
    supporting_belief_ids: [],
    cta: "",
    notes: "",
    status: "Planned",
  });
  const [beliefQuery, setBeliefQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchSchema().then(setSchema).catch(() => {});
    fetchBeliefs().then(setBeliefs).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (promotion) {
      setForm({
        month: promotion.month || currentYM(),
        promotion_name: promotion.promotion_name || "",
        offer_tag: promotion.offer_tag || "",
        headline_belief_id: promotion.headline_belief_id || "",
        supporting_belief_ids: promotion.supporting_belief_ids || [],
        cta: promotion.cta || "",
        notes: promotion.notes || "",
        status: promotion.status || "Planned",
      });
    } else {
      setForm({
        month: currentYM(),
        promotion_name: "",
        offer_tag: "",
        headline_belief_id: "",
        supporting_belief_ids: [],
        cta: "",
        notes: "",
        status: "Planned",
      });
    }
    setBeliefQuery("");
  }, [promotion, open]);

  const beliefMap = useMemo(() => {
    const m = {};
    beliefs.forEach((b) => (m[b.id] = b));
    return m;
  }, [beliefs]);

  const filteredBeliefs = useMemo(() => {
    const q = beliefQuery.trim().toLowerCase();
    return beliefs
      .filter((b) => !q || b.belief_text.toLowerCase().includes(q))
      .slice(0, 20);
  }, [beliefs, beliefQuery]);

  const toggleSupport = (id) => {
    setForm((f) => ({
      ...f,
      supporting_belief_ids: f.supporting_belief_ids.includes(id)
        ? f.supporting_belief_ids.filter((x) => x !== id)
        : [...f.supporting_belief_ids, id],
    }));
  };

  const save = async () => {
    if (!form.promotion_name.trim()) {
      toast.error("Promotion name is required.");
      return;
    }
    if (!form.month || !/^\d{4}-\d{2}$/.test(form.month)) {
      toast.error("Month must be in YYYY-MM format.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        month: form.month,
        promotion_name: form.promotion_name.trim(),
        offer_tag: form.offer_tag || null,
        headline_belief_id: form.headline_belief_id || null,
        supporting_belief_ids: form.supporting_belief_ids,
        cta: form.cta || "",
        notes: form.notes || "",
        status: form.status,
      };
      const result = isEdit
        ? await updatePromotion(promotion.id, payload)
        : await createPromotion(payload);
      toast.success(isEdit ? "Promotion updated." : "Promotion created.");
      onSaved?.(result);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm max-h-[90vh] overflow-y-auto"
        data-testid="promotion-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#141B4D] text-xl font-bold tracking-tight">
            {isEdit ? "Edit promotion" : "New promotion"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Month *
              </label>
              <input
                data-testid="promotion-month-input"
                type="month"
                value={form.month}
                onChange={(e) => setForm({ ...form, month: e.target.value })}
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Status
              </label>
              <select
                data-testid="promotion-status-select"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              >
                {(schema?.promotion_statuses || ["Planned", "Active", "Complete"]).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Promotion name *
            </label>
            <input
              data-testid="promotion-name-input"
              value={form.promotion_name}
              onChange={(e) =>
                setForm({ ...form, promotion_name: e.target.value })
              }
              placeholder="PowerBrand June Launch"
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Offer tag
            </label>
            <select
              data-testid="promotion-offer-tag-select"
              value={form.offer_tag}
              onChange={(e) => setForm({ ...form, offer_tag: e.target.value })}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            >
              <option value="">—</option>
              {(schema?.offer_tags || []).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Headline belief
            </label>
            <select
              data-testid="promotion-headline-belief-select"
              value={form.headline_belief_id}
              onChange={(e) =>
                setForm({ ...form, headline_belief_id: e.target.value })
              }
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            >
              <option value="">— Pick a belief —</option>
              {beliefs.map((b) => (
                <option key={b.id} value={b.id}>
                  [{b.category || "?"}] {b.belief_text.length > 80 ? b.belief_text.slice(0, 80) + "…" : b.belief_text}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Supporting beliefs ({form.supporting_belief_ids.length} selected)
            </label>
            {form.supporting_belief_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {form.supporting_belief_ids.map((id) => {
                  const b = beliefMap[id];
                  if (!b) return null;
                  return (
                    <span
                      key={id}
                      data-testid={`promotion-supporting-${id}`}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs bg-[#141B4D] text-[#F2E9DB]"
                    >
                      <span className="max-w-[260px] truncate">{b.belief_text}</span>
                      <button
                        type="button"
                        onClick={() => toggleSupport(id)}
                        className="hover:opacity-70"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#141B4D]/50"
              />
              <input
                data-testid="promotion-supporting-search"
                value={beliefQuery}
                onChange={(e) => setBeliefQuery(e.target.value)}
                placeholder="Search beliefs to link as supporting…"
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div className="mt-2 max-h-44 overflow-y-auto border border-[#141B4D]/15 rounded-sm bg-white">
              {filteredBeliefs.length === 0 ? (
                <div className="px-3 py-3 text-xs text-[#141B4D]/50">No beliefs match.</div>
              ) : (
                filteredBeliefs.map((b) => {
                  const active = form.supporting_belief_ids.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleSupport(b.id)}
                      data-testid={`promotion-supporting-option-${b.id}`}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-[#141B4D]/10 hover:bg-[#141B4D]/5 ${
                        active ? "bg-[#FFD100]/30" : ""
                      }`}
                    >
                      <span className="text-[#141B4D]">{b.belief_text}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              CTA
            </label>
            <input
              data-testid="promotion-cta-input"
              value={form.cta}
              onChange={(e) => setForm({ ...form, cta: e.target.value })}
              placeholder="Comment LAUNCH for early access"
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Notes
            </label>
            <textarea
              data-testid="promotion-notes-input"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            data-testid="promotion-cancel-button"
            className="px-5 py-2 rounded-sm text-sm border border-[#141B4D]/20 text-[#141B4D] hover:bg-[#141B4D]/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="promotion-save-button"
            className="px-5 py-2 rounded-sm text-sm bg-[#141B4D] text-[#F2E9DB] font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create promotion"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
